import { z } from 'zod';
import { DomainError } from '../errors.js';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';

/**
 * Admin panelinde tedarikçi yönetimi, belge doğrulaması ve partner yorumu (ADR 0120; sahip
 * maddesi `docs/durum.md` Grup 4). Saf kurallar: I/O yok, saat parametreyle gelir.
 */

/** Portal hesabının durumu: `disabled` CLI'nin kapatmasıdır (ADR 0112 §11). */
export const SUPPLIER_ACCOUNT_STATUSES = ['active', 'suspended', 'banned', 'disabled'] as const;
export type SupplierAccountStatus = (typeof SUPPLIER_ACCOUNT_STATUSES)[number];

export const SUPPLIER_ADMIN_ACTIONS = ['suspend', 'reinstate', 'ban', 'unban'] as const;
export type SupplierAdminAction = (typeof SUPPLIER_ADMIN_ACTIONS)[number];

/** Gerekçe sınırı (her yönetici işlemi gerekçeli; ADR 0120 §1). */
export const SUPPLIER_ADMIN_REASON = { min: 5, max: 500 } as const;

const TRANSITIONS: Readonly<
  Record<SupplierAdminAction, Partial<Record<SupplierAccountStatus, SupplierAccountStatus>>>
> = {
  suspend: { active: 'suspended' },
  reinstate: { suspended: 'active' },
  ban: { active: 'banned', suspended: 'banned', disabled: 'banned' },
  // Yasak kalkınca hesap kendiliğinden açılmaz: ayrı "reinstate" ister [sorular #1441]
  unban: { banned: 'suspended' },
};

/** Durum geçişi (ADR 0120 §1); geçersizse `INVALID_TRANSITION`. */
export function nextSupplierAccountStatus(
  current: SupplierAccountStatus,
  action: SupplierAdminAction,
): SupplierAccountStatus {
  const next = TRANSITIONS[action][current];
  if (!next) throw new DomainError('INVALID_TRANSITION', undefined, `${current}:${action}`);
  return next;
}

/**
 * Yeniden kayıt engelinin anahtarı (ADR 0120 §2) [sorular #1445]: küçük harf, kırpılmış, yerel
 * kısımdaki `+etiket` atılmış. Nokta ve alan adı takma adları çözülmez. Aynı kural control_db'de
 * `supplier_canonical_email()` SQL işlevidir (migration `0012`).
 */
export function canonicalSupplierEmail(email: string): string {
  const value = email.trim().toLowerCase();
  const at = value.lastIndexOf('@');
  if (at <= 0) return value;
  const local = value.slice(0, at);
  const plus = local.indexOf('+');
  return plus > 0 ? `${local.slice(0, plus)}${value.slice(at)}` : value;
}

// --- Belgeler ve Verified ---

export const SUPPLIER_DOCUMENT_TYPES = [
  'company_registration',
  'tax_certificate',
  'quality_certificate',
  'insurance',
  'bank_letter',
  'other',
] as const;
export type SupplierDocumentType = (typeof SUPPLIER_DOCUMENT_TYPES)[number];

export type SupplierDocumentDecision = 'approved' | 'rejected';
export type SupplierDocumentRequestStatus =
  | 'requested'
  | 'submitted'
  | 'approved'
  | 'rejected'
  | 'cancelled';

/** İsteğin durumu son yüklenen belgeden ve kararından türer (ADR 0120 §3). */
export function supplierDocumentRequestStatus(input: {
  cancelled: boolean;
  latest: { decision: SupplierDocumentDecision | null } | null;
}): SupplierDocumentRequestStatus {
  if (input.cancelled) return 'cancelled';
  if (!input.latest) return 'requested';
  return input.latest.decision ?? 'submitted';
}

/** Yükleme yalnız açık istekte ve belge yoksa ya da reddedildiyse. */
export function documentUploadExpected(status: SupplierDocumentRequestStatus): boolean {
  return status === 'requested' || status === 'rejected';
}

/**
 * "Verified" verilebilir mi (ADR 0120 §3) [sorular #1448]: hesap etkin, en az bir istek var ve
 * iptal edilmemiş bütün isteklerin son belgesi onaylı. Uygunsa `null`, değilse hata kodu.
 */
export function verificationEligible(
  status: SupplierAccountStatus,
  requests: readonly SupplierDocumentRequestStatus[],
): 'ACCOUNT_NOT_ACTIVE' | 'DOCUMENTS_INCOMPLETE' | null {
  if (status !== 'active') return 'ACCOUNT_NOT_ACTIVE';
  const live = requests.filter((request) => request !== 'cancelled');
  if (live.length === 0 || live.some((request) => request !== 'approved')) {
    return 'DOCUMENTS_INCOMPLETE';
  }
  return null;
}

// --- Partner yorumu ---

/**
 * Yorum yazılabilen sipariş (ADR 0120 §4) [sorular #1452]: teslim alınmış (`delivered`) ya da
 * teslimi olan kapanmış (`completed`) PO. Teslimsiz kapanan ve iptal edilen sayılmaz.
 */
export function reviewOrderEligible(
  status: string,
  delivery: 'none' | 'partial' | 'full',
): boolean {
  if (status === 'delivered') return true;
  return status === 'completed' && delivery !== 'none';
}

export const SUPPLIER_REVIEW_COMMENT = { min: 10, max: 2000 } as const;

export const supplierReviewSchema = z.strictObject({
  rating: z
    .number({ error: 'INVALID_RATING' })
    .int('INVALID_RATING')
    .min(1, 'INVALID_RATING')
    .max(5, 'INVALID_RATING'),
  comment: z
    .string({ error: 'INVALID_TEXT' })
    .transform((value) => value.trim())
    .pipe(
      z
        .string()
        .min(SUPPLIER_REVIEW_COMMENT.min, 'TOO_SHORT')
        .max(SUPPLIER_REVIEW_COMMENT.max, 'TOO_LONG'),
    ),
});
export type SupplierReviewInput = z.output<typeof supplierReviewSchema>;

export function parseSupplierReview(input: unknown): ParseResult<SupplierReviewInput> {
  const result = supplierReviewSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

export const SUPPLIER_REVIEW_DECISIONS = ['approved', 'rejected', 'withdrawn'] as const;
export type SupplierReviewDecision = (typeof SUPPLIER_REVIEW_DECISIONS)[number];
/** Yorumun yayın durumu: kararı yoksa bekliyor. */
export type SupplierReviewStatus = 'pending' | SupplierReviewDecision;

/**
 * Admin kararı (ADR 0120 §4) [sorular #1455]: ilk karar onay ya da ret; onaylı yorum gerekçeyle
 * yayından kaldırılır. Reddedilen ve kaldırılan son durumdur.
 */
export function nextReviewDecisionState(
  current: SupplierReviewDecision | null,
  decision: SupplierReviewDecision,
): SupplierReviewDecision {
  const ok =
    current === null
      ? decision === 'approved' || decision === 'rejected'
      : current === 'approved' && decision === 'withdrawn';
  if (!ok)
    throw new DomainError('INVALID_TRANSITION', undefined, `${current ?? 'pending'}:${decision}`);
  return decision;
}
